import * as React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

import { cn } from "@/lib/utils";

/**
 * `true` enquanto o conteúdo do viewport passa da altura visível. Mede com ResizeObserver no viewport e no
 * conteúdo (o `type="auto"` do Radix não detectava o transbordo quando o limite vem de `max-h-*`).
 */
function useTransbordaVertical(viewportRef: React.RefObject<HTMLDivElement>, ativo: boolean): boolean {
  const [transborda, setTransborda] = React.useState(false);
  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (!ativo || !viewport || typeof ResizeObserver === "undefined") {
      setTransborda(false);
      return;
    }
    const medir = () => setTransborda(viewport.scrollHeight > viewport.clientHeight + 1);
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(viewport);
    if (viewport.firstElementChild) observador.observe(viewport.firstElementChild);
    return () => observador.disconnect();
  }, [viewportRef, ativo]);
  return transborda;
}

interface ScrollAreaProps extends React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  /**
   * Barra sempre visível quando há o que rolar (e escondida quando o conteúdo cabe) — no macOS a barra só
   * aparece ao passar o mouse/rolar. Com isso a área também recebe foco pelo teclado (setas rolam) quando rola.
   */
  barraQuandoTransborda?: boolean;
  /** Nome da área rolável para leitores de tela (usado quando ela recebe foco). */
  rotuloRolagem?: string;
}

const ScrollArea = React.forwardRef<React.ElementRef<typeof ScrollAreaPrimitive.Root>, ScrollAreaProps>(
  ({ className, children, barraQuandoTransborda = false, rotuloRolagem, type, ...props }, ref) => {
    const viewportRef = React.useRef<HTMLDivElement>(null);
    const transborda = useTransbordaVertical(viewportRef, barraQuandoTransborda);
    const focavel = barraQuandoTransborda && transborda;
    return (
      <ScrollAreaPrimitive.Root
        ref={ref}
        type={barraQuandoTransborda ? (transborda ? "always" : "hover") : type}
        className={cn("relative overflow-hidden", className)}
        {...props}
      >
        {/* max-h-[inherit]: com só `max-h-*` no Root (sem altura fixa), o Viewport herda o limite e passa a rolar. */}
        <ScrollAreaPrimitive.Viewport
          ref={viewportRef}
          tabIndex={focavel ? 0 : undefined}
          role={focavel ? "region" : undefined}
          aria-label={focavel ? rotuloRolagem : undefined}
          className="h-full max-h-[inherit] w-full rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {children}
        </ScrollAreaPrimitive.Viewport>
        <ScrollBar />
        <ScrollAreaPrimitive.Corner />
      </ScrollAreaPrimitive.Root>
    );
  },
);
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = "vertical", ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      "flex touch-none select-none transition-colors",
      orientation === "vertical" && "h-full w-2.5 border-l border-l-transparent p-[1px]",
      orientation === "horizontal" && "h-2.5 flex-col border-t border-t-transparent p-[1px]",
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
